<template>
  <div>
    <div :class="[containerClass, 'config-wide-container']">
      <page-header
        :title="'Configure Grading Criteria'"
        :container-class="null"
        :info-text="'Configure grading weightages for this cohort'"
        :show-back="true"
        class="mb-32pt"
      />
    </div>

    <div class="page-section">
      <div :class="[containerClass, 'config-wide-container']">
        <div v-if="isLoading" class="loading-container">
          <b-spinner class="align-middle mr-2"></b-spinner>
          <strong>Loading configuration...</strong>
        </div>

        <div v-else-if="configData" class="config-container">
          
          <div class="config-actions-top">
            <div v-if="tagName" class="tag-name">
              {{ tagName }}
            </div>
            <button
              v-if="configData.snapshot_exists"
              class="btn-secondary reset-btn"
              :disabled="isLoading"
              @click="handleResetToDefault"
            >
              Reset to default
            </button>
            <span class="meta-badge meta-badge-warning" v-else>
              <i class="material-icons">warning</i>
              Not Saved
            </span>
          </div>
          <div class="config-helper-text" v-if="!configData.snapshot_exists">
            Configuring Grading criteria grades students associated with this class according to the criteria defined, once you define the criteria, change in default weightage will not affect the student grades
          </div>

          <div class="config-body">
            <div class="weightages-section">
              <div class="table-container">
                <table class="weightage-table">
                  <thead>
                    <tr>
                      <th class="col-item">ITEM</th>
                      <th class="col-type">TYPE</th>
                      <th class="col-weightage">WEIGHTAGE</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="row in tableRows"
                      :key="row.key"
                      :class="row.rowClass"
                    >
                      <td class="item-cell">
                        <div class="item-content" :class="row.contentClass">
                          <div v-if="row.badge" :class="row.badgeClass">{{ row.badge }}</div>
                          <i v-if="row.icon" class="material-icons item-icon" :class="row.iconClass">{{ row.icon }}</i>
                          <div class="item-info">
                            <div class="item-title">{{ row.title }}</div>
                            <div v-if="row.subtitle" class="item-subtitle">{{ row.subtitle }}</div>
                            <div v-if="row.linkedTo && row.linkedTo.length" class="item-subtitle linked-to">
                              <div class="linked-to-label">Linked to:</div>
                              <div
                                v-for="(loc, idx) in row.linkedTo"
                                :key="`${row.key}-loc-${idx}`"
                                class="linked-to-item"
                              >
                                {{ loc }}
                              </div>
                            </div>
                          </div>
                        </div>
                      </td>
                      <td class="type-cell">
                        <span
                          v-if="['Assignment', 'Assessment', 'Final Exam'].includes(row.type)"
                          class="type-badge"
                          :class="row.typeClass"
                        >
                          <i v-if="row.typeIcon" class="material-icons">{{ row.typeIcon }}</i>
                          {{ row.type }}
                        </span>
                        <span v-else-if="!row.type" class="type-empty"></span>
                        <span v-else class="type-text">{{ row.type }}</span>
                      </td>
                      <td class="weightage-cell">
                        <div v-if="row.editable" class="weightage-input-wrapper">
                          <input
                            type="number"
                            v-model.number="row.weightageObj.weightage"
                            min="0"
                            max="100"
                            step="0.1"
                            class="weightage-input"
                            :disabled="!!row.inputDisabled"
                            @input="validateWeightage(row.weightageObj)"
                          />
                          <span class="input-suffix">%</span>
                        </div>
                        <span v-else>—</span>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              
            </div>

            
          </div>

          <div class="config-actions-bottom">
            <button @click="goBack" class="btn-secondary">
              Cancel
            </button>
            <button
              @click="handleSave"
              :disabled="isSaving || !isValidWeightages"
              class="btn-primary"
            >
              <b-spinner v-if="isSaving" small class="mr-2"></b-spinner>
              {{ isSaving ? 'Saving...' : 'Save Configuration' }}
            </button>
          </div>
        </div>

        <div v-else-if="error" class="error-container">
          <i class="material-icons error-icon">error_outline</i>
          <p class="error-message">{{ error }}</p>
          <button @click="fetchConfigData" class="btn-retry">
            <i class="material-icons">refresh</i>
            Retry
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions } from 'vuex';
import Page from '@/components/Page.vue';
import PageHeader from '@/components/Ui/PageHeader.vue';

export default {
  components: {
    PageHeader,
  },
  extends: Page,

  data() {
    return {
      title: 'Configure Cohort',
      courseId: null,
      tagId: null,
      tagTitle: '',
      isLoading: false,
      isSaving: false,
      configData: null,
      editableWeightages: {
        quiz_weightages: [],
        assignment_weightages: [],
      },
      createSnapshot: true,
      error: null,
    };
  },

  computed: {
    tagName() {
      return (
        this.configData?.tag_title ||
        this.configData?.tag?.title ||
        this.tagTitle ||
        ''
      );
    },
    chapters() {
      if (this.configData && this.configData.course_structure && this.configData.course_structure.chapters) {
        return this.configData.course_structure.chapters;
      }
      return [];
    },
    finalExam() {
      if (this.configData && this.configData.course_structure && this.configData.course_structure.course) {
        return this.configData.course_structure.course.final_exam || null;
      }
      return null;
    },
    totalQuizWeightage() {
      return this.editableWeightages.quiz_weightages.reduce(
        (sum, quiz) => sum + (parseFloat(quiz.weightage) || 0),
        0
      );
    },
    totalAssignmentWeightage() {
      return this.editableWeightages.assignment_weightages.reduce(
        (sum, assignment) => sum + (parseFloat(assignment.weightage) || 0),
        0
      );
    },
    totalWeightage() {
      return this.totalQuizWeightage + this.totalAssignmentWeightage;
    },
    isValidWeightages() {
      return this.totalWeightage <= 100;
    },
    tableRows() {
      const rows = [];

      const itemsByKey = new Map();
      const ensureItem = ({ key, id, title, icon, iconClass, type, typeClass, typeIcon, subtitle, weightageObj, editable = true, inputDisabled = false }) => {
        if (!itemsByKey.has(key)) {
          itemsByKey.set(key, {
            key,
            id,
            title,
            icon,
            iconClass,
            type,
            typeClass,
            typeIcon,
            subtitle,
            weightageObj,
            editable,
            inputDisabled,
            linkedTo: [],
          });
        }
        return itemsByKey.get(key);
      };

      const addLinkedTo = (item, loc) => {
        if (!loc) return;
        if (!item.linkedTo.includes(loc)) item.linkedTo.push(loc);
      };

      this.chapters.forEach((chapter) => {
        if (chapter.quiz) {
          const item = ensureItem({
            key: `quiz-${chapter.quiz.id}`,
            id: chapter.quiz.id,
            title: chapter.quiz.title,
            icon: 'help_outline',
            iconClass: 'icon-quiz',
            type: 'Assessment',
            typeClass: 'type-assessment',
            typeIcon: null,
            subtitle: null,
            weightageObj: this.getQuizWeightage(chapter.quiz.id),
          });
          addLinkedTo(item, `Chapter: ${chapter.title}`);
        }

        (chapter.lessons || []).forEach((lesson) => {
          if (lesson.quiz) {
            const item = ensureItem({
              key: `quiz-${lesson.quiz.id}`,
              id: lesson.quiz.id,
              title: lesson.quiz.title,
              icon: 'help_outline',
              iconClass: 'icon-quiz',
              type: 'Assessment',
              typeClass: 'type-assessment',
              typeIcon: null,
              subtitle: null,
              weightageObj: this.getQuizWeightage(lesson.quiz.id),
              editable: true,
              inputDisabled: false,
            });
            addLinkedTo(item, `Chapter: ${chapter.title} / Lesson: ${lesson.title}`);
          }

          if (lesson.assignment) {
            const isEditable = lesson.assignment.is_graded !== false;
            const item = ensureItem({
              key: `assignment-${lesson.assignment.id}`,
              id: lesson.assignment.id,
              title: lesson.assignment.title,
              icon: 'description',
              iconClass: 'icon-assignment',
              type: 'Assignment',
              typeClass: 'type-assignment',
              typeIcon: null,
              subtitle: null,
              weightageObj: isEditable ? this.getAssignmentWeightage(lesson.assignment.id) : null,
              editable: isEditable,
              inputDisabled: !isEditable,
            });
            addLinkedTo(item, `Chapter: ${chapter.title} / Lesson: ${lesson.title}`);
          }
        });
      });

      if (this.finalExam) {
        const item = ensureItem({
          key: `final-exam-${this.finalExam.id}`,
          id: this.finalExam.id,
          title: this.finalExam.title,
          icon: 'emoji_events',
          iconClass: 'icon-final-exam',
          type: 'Final Exam',
          typeClass: 'type-exam',
          typeIcon: null,
          subtitle: 'Final assessment',
          weightageObj: this.getQuizWeightage(this.finalExam.id),
        });
        addLinkedTo(item, 'Course: Final Exam');
      }

      for (const item of itemsByKey.values()) {
        rows.push({
          key: item.key,
          rowClass: 'assessment-row',
          contentClass: '',
          badge: null,
          badgeClass: null,
          icon: item.icon,
          iconClass: item.iconClass,
          title: item.title,
          subtitle: item.subtitle,
          linkedTo: item.linkedTo,
          type: item.type,
          typeClass: item.typeClass,
          typeIcon: item.typeIcon,
          editable: item.editable !== false,
          weightageObj: item.weightageObj,
          inputDisabled: item.inputDisabled,
          subtotal: '',
        });
      }

      return rows;
    },
  },

  methods: {
    ...mapActions('courses', ['getCohortCourseConfig', 'setupCohortCourseConfig']),
    ...mapActions('attendance', ['getTags']),

    async fetchTagTitle() {
      if (!this.tagId) return;
      try {
        const response = await this.getTags({ id: this.tagId, limit: 1 });
        const tag = response?.data?.data?.[0];
        this.tagTitle = tag?.title || '';
      } catch (e) {
        this.tagTitle = '';
      }
    },

    async fetchConfigData({ resetToDefault = false } = {}) {
      if (!this.tagId || !this.courseId) {
        this.error = 'Tag ID and Course ID are required';
        return;
      }

      this.isLoading = true;
      this.error = null;

      try {
        const params = {
          tag_id: this.tagId,
          course_id: this.courseId,
        };

        if (resetToDefault) {
          params.reset_to_default = true;
        }

        const response = await this.getCohortCourseConfig(params);
        
        if (response && response.course_structure) {
          this.configData = response;
          this.createSnapshot = response.create_snapshot !== false;
          
          this.initializeWeightages(response);
        } else {
          this.error = 'Failed to load configuration data';
        }
      } catch (e) {
        this.error = (e.response && e.response.data && e.response.data.message) || 'Failed to load configuration data';
      } finally {
        this.isLoading = false;
      }
    },

    handleResetToDefault() {
      this.fetchConfigData({ resetToDefault: true });
    },

    validateWeightage(item) {
      if (item.weightage < 0) {
        item.weightage = 0;
      } else if (item.weightage > 100) {
        item.weightage = 100;
      }
    },

    async handleSave() {
      if (!this.isValidWeightages) {
        this.makeToast({
          variant: 'warning',
          msg: 'Total weightages cannot exceed 100%',
        });
        return;
      }

      this.isSaving = true;

      try {
        const payload = {
          tag_id: this.tagId,
          course_id: this.courseId,
          create_snapshot: this.createSnapshot,
          grading_weightages: {
            quiz_weightages: this.editableWeightages.quiz_weightages.map((q) => ({
              quiz_id: q.quiz_id,
              weightage: parseFloat(q.weightage) || 0,
            })),
            assignment_weightages: this.editableWeightages.assignment_weightages.map((a) => ({
              assignment_id: a.assignment_id,
              weightage: parseFloat(a.weightage) || 0,
            })),
          },
        };

        await this.setupCohortCourseConfig(payload);
        
        this.makeToast({
          variant: 'success',
          msg: 'Configuration saved successfully',
        });

        // Refetch the configuration data to get updated values
        await this.fetchConfigData();
      } catch (e) {
        this.makeToast({
          variant: 'danger',
          msg: (e.response && e.response.data && e.response.data.message) || 'Failed to save configuration',
        });
      } finally {
        this.isSaving = false;
      }
    },

    goBack() {
      this.$router.go(-1);
    },

    getQuizWeightage(quizId) {
      return this.editableWeightages.quiz_weightages.find(q => q.quiz_id === quizId) || { weightage: 0 };
    },

    getAssignmentWeightage(assignmentId) {
      return this.editableWeightages.assignment_weightages.find(a => a.assignment_id === assignmentId) || { weightage: 0 };
    },

    ensureWeightageEntries(courseStructure) {
      const quizIds = new Set(this.editableWeightages.quiz_weightages.map((q) => q.quiz_id));
      const assignmentIds = new Set(this.editableWeightages.assignment_weightages.map((a) => a.assignment_id));

      if (courseStructure && courseStructure.course && courseStructure.course.final_exam) {
        const finalExam = courseStructure.course.final_exam;
        if (!quizIds.has(finalExam.id)) {
          this.editableWeightages.quiz_weightages.push({
            quiz_id: finalExam.id,
            quiz_title: finalExam.title,
            weightage: parseFloat(finalExam.weightage) || 0,
          });
          quizIds.add(finalExam.id);
        }
      }

      (courseStructure && courseStructure.chapters ? courseStructure.chapters : []).forEach((chapter) => {
        if (chapter.quiz && !quizIds.has(chapter.quiz.id)) {
          this.editableWeightages.quiz_weightages.push({
            quiz_id: chapter.quiz.id,
            quiz_title: chapter.quiz.title,
            weightage: parseFloat(chapter.quiz.weightage) || 0,
          });
          quizIds.add(chapter.quiz.id);
        }

        (chapter.lessons || []).forEach((lesson) => {
          if (lesson.quiz && !quizIds.has(lesson.quiz.id)) {
            this.editableWeightages.quiz_weightages.push({
              quiz_id: lesson.quiz.id,
              quiz_title: lesson.quiz.title,
              weightage: parseFloat(lesson.quiz.weightage) || 0,
            });
            quizIds.add(lesson.quiz.id);
          }

          if (lesson.assignment && lesson.assignment.is_graded !== false && !assignmentIds.has(lesson.assignment.id)) {
            this.editableWeightages.assignment_weightages.push({
              assignment_id: lesson.assignment.id,
              assignment_title: lesson.assignment.title,
              weightage: parseFloat(lesson.assignment.weightage) || 0,
            });
            assignmentIds.add(lesson.assignment.id);
          }
        });
      });
    },

    initializeWeightages(response) {
      const showDefault = response.show_default;
      
      if (showDefault) {
        this.editableWeightages = this.extractWeightagesFromCourseStructure(response.course_structure);
      } else {
        const gradingWeightages = response.grading_weightages || {};
        this.editableWeightages = {
          quiz_weightages: JSON.parse(
            JSON.stringify(gradingWeightages.quiz_weightages || [])
          ),
          assignment_weightages: JSON.parse(
            JSON.stringify(gradingWeightages.assignment_weightages || [])
          ),
        };
      }

      this.ensureWeightageEntries(response.course_structure);
    },

    extractWeightagesFromCourseStructure(courseStructure) {
      const quizWeightages = [];
      const assignmentWeightages = [];
      const processedQuizIds = new Set();
      const processedAssignmentIds = new Set();

      if (courseStructure.course && courseStructure.course.final_exam) {
        const finalExam = courseStructure.course.final_exam;
        quizWeightages.push({
          quiz_id: finalExam.id,
          quiz_title: finalExam.title,
          weightage: parseFloat(finalExam.weightage) || 0,
        });
        processedQuizIds.add(finalExam.id);
      }

      if (courseStructure.chapters) {
        courseStructure.chapters.forEach((chapter) => {
          if (chapter.quiz && !processedQuizIds.has(chapter.quiz.id)) {
            quizWeightages.push({
              quiz_id: chapter.quiz.id,
              quiz_title: chapter.quiz.title,
              weightage: parseFloat(chapter.quiz.weightage) || 0,
            });
            processedQuizIds.add(chapter.quiz.id);
          }

          if (chapter.lessons) {
            chapter.lessons.forEach((lesson) => {
              if (lesson.quiz && !processedQuizIds.has(lesson.quiz.id)) {
                quizWeightages.push({
                  quiz_id: lesson.quiz.id,
                  quiz_title: lesson.quiz.title,
                  weightage: parseFloat(lesson.quiz.weightage) || 0,
                });
                processedQuizIds.add(lesson.quiz.id);
              }

              if (lesson.assignment && lesson.assignment.is_graded !== false && !processedAssignmentIds.has(lesson.assignment.id)) {
                assignmentWeightages.push({
                  assignment_id: lesson.assignment.id,
                  assignment_title: lesson.assignment.title,
                  weightage: parseFloat(lesson.assignment.weightage) || 0,
                });
                processedAssignmentIds.add(lesson.assignment.id);
              }
            });
          }
        });
      }

      return {
        quiz_weightages: quizWeightages,
        assignment_weightages: assignmentWeightages,
      };
    },
  },

  mounted() {
    this.courseId = this.$route.params.courseId;
    this.tagId = this.$route.params.tagId;

    if (!this.courseId || !this.tagId) {
      this.makeToast({ variant: 'danger', msg: 'Course ID and Tag ID are required' });
      this.$router.go(-1);
      return;
    }

    this.fetchTagTitle();
    this.fetchConfigData();
  },
};
</script>

<style scoped lang="scss">
.config-wide-container {
  max-width: 1400px;
}

.loading-container,
.error-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  min-height: 300px;
  background: #fff;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
}

.error-icon {
  font-size: 48px;
  color: #ef4444;
  margin-bottom: 16px;
}

.error-message {
  font-size: 16px;
  color: #6b7280;
  margin-bottom: 20px;
  text-align: center;
}

.btn-retry {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  background: #fff;
  color: #374151;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: #f9fafb;
    border-color: #9ca3af;
  }

  i {
    font-size: 18px;
  }
}

.config-container {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.config-actions-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.tag-name {
  font-size: 1.2rem;
  font-weight: 700;
  color: #111827;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.config-helper-text {
  width: 100%;
  padding: 12px 16px;
  font-size: 0.95rem;
  font-weight: 600;
  color: #1e3a8a;
  background: #eff6ff;
  border: 1px solid #93c5fd;
  border-left: 4px solid #2563eb;
  border-radius: 8px;
}

.config-actions-bottom {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
}

.reset-btn {
  padding: 6px 14px;
  border-radius: 12px;
  font-size: 13px;
  font-weight: 600;
}

.meta-badge {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 14px;
  border-radius: 12px;
  font-size: 13px;
  font-weight: 600;
  background: #d1fae5;
  color: #047857;

  i {
    font-size: 18px;
  }

  &.meta-badge-warning {
    background: #fef3c7;
    color: #92400e;
  }
}

.config-body {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.weightages-section {
  background: #fff;
  border-radius: 12px;
  border: 1px solid #e5e7eb;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
  overflow: hidden;
}

.table-container {
  overflow-x: auto;
}

.weightage-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;

  thead {
    background: #f9fafb;
    border-bottom: 1px solid #e5e7eb;

    th {
      padding: 14px 20px;
      text-align: left;
      font-size: 11px;
      font-weight: 700;
      color: #6b7280;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      white-space: nowrap;

      &.col-item {
        width: 55%;
      }

      &.col-type {
        width: 20%;
      }

      &.col-weightage {
        width: 25%;
        text-align: center;
      }
    }
  }

  tbody {
    tr {
      border-bottom: 1px solid #f3f4f6;
      transition: background-color 0.15s ease;

      &:last-child {
        border-bottom: none;
      }

      &:hover {
        background: #fafbfc;
      }

      &.assessment-row {
        background: #fff;
      }

      &.final-exam-row {
        background: #fafbfc;
        border-top: 2px solid #e5e7eb;
      }
    }

    td {
      padding: 12px 20px;
      vertical-align: middle;
    }
  }
}

.item-cell {
  .item-content {
    display: flex;
    align-items: center;
    gap: 10px;
  }

  .chapter-badge,
  .lesson-badge {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    border-radius: 8px;
    font-size: 13px;
    font-weight: 700;
    flex-shrink: 0;
  }

  .chapter-badge {
    background: #3B86F5;
    color: #fff;

    &.exam-badge {
      background: #181818;
    }
  }

  .item-icon {
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    font-size: 18px;
    flex-shrink: 0;

    &.icon-quiz {
      background: rgba(59, 134, 245, 0.15);
      color: #3B86F5;
    }

    &.icon-assignment {
      background: rgba(24, 24, 24, 0.08);
      color: #181818;
    }

    &.icon-final-exam {
      background: rgba(245, 158, 11, 0.18);
      color: #b45309;
    }
  }

  .item-info {
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
  }

  .item-title {
    font-size: 14px;
    font-weight: 600;
    color: #1f2937;
    line-height: 1.4;
  }

  .item-subtitle {
    font-size: 12px;
    color: #9ca3af;
    line-height: 1.3;
  }

  .linked-to {
    margin-top: 4px;
  }

  .linked-to-label {
    font-weight: 600;
    color: #6b7280;
  }

  .linked-to-item {
    margin-top: 2px;
    color: #9ca3af;
  }
}

.type-cell {
  .type-empty {
    display: inline-block;
    min-height: 18px;
  }

  .type-text {
    display: inline-flex;
    align-items: center;
    font-size: 12px;
    font-weight: 600;
    color: #181818;
    white-space: nowrap;
  }

  .type-badge {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 6px 12px;
    border-radius: 6px;
    font-size: 12px;
    font-weight: 600;
    white-space: nowrap;

    i {
      font-size: 16px;
    }

    &.type-chapter {
      background: rgba(59, 134, 245, 0.1);
      color: #3B86F5;
      border: 1px solid rgba(59, 134, 245, 0.3);
    }

    &.type-lesson {
      background: rgba(24, 24, 24, 0.05);
      color: #181818;
      border: 1px solid rgba(24, 24, 24, 0.15);
    }

    &.type-assessment {
      background: linear-gradient(135deg, #eef2ff 0%, #e0e7ff 100%);
      color: #4338ca;
      border: 1px solid #c7d2fe;
    }

    &.type-assignment {
      background: linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%);
      color: #047857;
      border: 1px solid #a7f3d0;
    }

    &.type-exam {
      background: rgba(24, 24, 24, 0.05);
      color: #181818;
      border: 1px solid rgba(24, 24, 24, 0.15);
    }
  }
}

.weightage-cell {
  text-align: center;
  color: #9ca3af;
  font-weight: 500;

  .weightage-input-wrapper {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    background: #f9fafb;
    border: 1px solid #e5e7eb;
    border-radius: 6px;
    padding: 6px 12px;
    transition: all 0.15s ease;

    &:focus-within {
      background: #fff;
      border-color: #3B86F5;
      box-shadow: 0 0 0 3px rgba(59, 134, 245, 0.1);
    }

    .weightage-input {
      width: 50px;
      border: none;
      background: transparent;
      font-size: 14px;
      font-weight: 600;
      color: #1f2937;
      text-align: right;
      padding: 0;

      &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
      }

      &:focus {
        outline: none;
      }

      &::-webkit-inner-spin-button,
      &::-webkit-outer-spin-button {
        -webkit-appearance: none;
        margin: 0;
      }

      &[type='number'] {
        appearance: textfield;
        -moz-appearance: textfield;
      }
    }

    .input-suffix {
      font-size: 13px;
      font-weight: 600;
      color: #9ca3af;
    }
  }
}

.weightage-summary {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 20px;
  background: #fafbfc;
  border-top: 1px solid #e5e7eb;

  .summary-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .summary-label {
    font-size: 14px;
    font-weight: 600;
    color: #6b7280;
  }

  .summary-value {
    font-size: 20px;
    font-weight: 700;
    color: #1f2937;

    &.text-danger {
      color: #dc2626;
    }
  }

  .summary-warning {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 12px 16px;
    background: #fef2f2;
    border: 1px solid #fecaca;
    border-radius: 8px;
    color: #dc2626;
    font-size: 13px;
    font-weight: 600;

    i {
      font-size: 18px;
    }
  }
}

.snapshot-option {
  padding: 18px;
  background: #fffbeb;
  border: 1px solid #fde68a;
  border-radius: 8px;
}

.checkbox-label {
  display: flex;
  align-items: center;
  gap: 12px;
  cursor: pointer;
  margin-bottom: 10px;
}

.checkbox-input {
  width: 20px;
  height: 20px;
  cursor: pointer;
}

.checkbox-text {
  font-size: 15px;
  font-weight: 600;
  color: #1f2937;
}

.option-description {
  font-size: 13px;
  color: #6b7280;
  margin: 0;
  margin-left: 32px;
}

.config-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 20px;
  background: #fff;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.btn-secondary,
.btn-primary {
  padding: 12px 24px;
  border-radius: 6px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;
  display: flex;
  align-items: center;
  gap: 8px;
}

.btn-secondary {
  background: #181818;
  color: #fff;
  border: none;

  &:hover {
    background: #0f0f0f;
  }
}

.btn-primary {
  background: #3B86F5;
  color: #fff;

  &:hover:not(:disabled) {
    background: #2a75e4;
    box-shadow: 0 4px 6px rgba(59, 134, 245, 0.25);
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
}

@media (max-width: 767.98px) {
  .header-info {
    flex-direction: column;
    align-items: flex-start;
  }

  .weightage-table {
    thead th {
      padding: 12px 16px;
      font-size: 10px;
    }

    tbody td {
      padding: 10px 16px;
    }
  }

  .item-cell {
    .item-content {
      &.nested-item {
        padding-left: 36px;
      }

      &.deeply-nested-item {
        padding-left: 72px;
      }
    }

    .chapter-badge,
    .lesson-badge {
      width: 28px;
      height: 28px;
      font-size: 11px;
    }

    .item-title {
      font-size: 13px;
    }

    .item-subtitle {
      font-size: 11px;
    }
  }

  .type-cell .type-badge {
    font-size: 11px;
    padding: 5px 10px;

    i {
      font-size: 14px;
    }
  }

  .weightage-cell .weightage-input-wrapper {
    padding: 5px 10px;

    .weightage-input {
      width: 40px;
      font-size: 13px;
    }
  }

  .config-footer {
    flex-direction: column-reverse;

    .btn-secondary,
    .btn-primary {
      width: 100%;
      justify-content: center;
    }
  }
}
</style>
